import {
  ChangeDetectionStrategy,
  Component,
  inject,
  WritableSignal,
  signal,
} from '@angular/core';
import {
  IconManager,
  iconQuote,
  iconShipment,
  SideNavigationComponent as TwcSideNavigationComponent,
  SideNavigationMenuItem,
} from '@tiltgroup/web-components';
import { MatDialog } from '@angular/material/dialog';
import { Permission } from '@shared/interfaces/permission';
import { SideNavigationStateService } from '@services/side-navigation-state.service';
import { AppRoutes } from '../../../app-routing';
import { QuotesRouting } from '../../../pages/quotes/quotes-routing';
import { QuoteManagerRouting } from '../../../pages/quotes/pages/quote-manager/quote-manager-routing';
import { ImportQuoteModalComponent } from '../../../pages/quotes/components/import-quote-modal/import-quote-modal.component';
import { ImportShipmentModalComponent } from '../../../pages/shipments/components/import-shipment-modal/import-shipment-modal.component';

@Component({
  selector: 'lig-side-navigation',
  imports: [TwcSideNavigationComponent],
  templateUrl: './side-navigation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavigationComponent {
  private _dialog = inject(MatDialog);
  private _iconManager = inject(IconManager);

  protected sidebarState = inject(SideNavigationStateService);

  protected readonly menuItems: WritableSignal<SideNavigationMenuItem[]> =
    signal(this._createMenuItems());

  protected readonly createAction: SideNavigationMenuItem = {
    name: 'Create',
    permission: [
      Permission.MANAGE_LTL_QUOTE_REQUEST,
      Permission.IMPORT_SHIPMENT,
    ],
    children: [
      {
        name: 'Create quote',
        url: `/${AppRoutes.QUOTES}/${QuotesRouting.QUOTE_MANAGER}/${QuoteManagerRouting.NEW_QUOTE}`,
        permission: Permission.MANAGE_LTL_QUOTE_REQUEST,
      },
      {
        name: 'Create quick quote',
        url: `/${AppRoutes.QUOTES}/${QuotesRouting.QUOTE_MANAGER}/${QuoteManagerRouting.QUICK_QUOTE}`,
        permission: Permission.MANAGE_LTL_QUOTE_REQUEST,
      },
      {
        name: 'Import quote',
        onClick: () => this.importQuote(),
        permission: Permission.MANAGE_LTL_QUOTE_REQUEST,
      },
      {
        name: 'Import shipment',
        onClick: () => this.importShipment(),
        permission: Permission.IMPORT_SHIPMENT,
      },
    ],
  };

  constructor() {
    this._iconManager.addIcons([
      { name: 'quote', icon: iconQuote },
      { name: 'shipment', icon: iconShipment },
    ]);
  }

  importQuote() {
    this._dialog.open(ImportQuoteModalComponent, {
      autoFocus: false,
      width: '520px',
    });
  }

  importShipment() {
    this._dialog.open(ImportShipmentModalComponent, {
      autoFocus: false,
      width: '520px',
    });
  }

  private _createMenuItems(): SideNavigationMenuItem[] {
    return [
      {
        name: 'Quotes',
        url: `/${AppRoutes.QUOTES}`,
        icon: 'twc:quote',
        permission: [
          Permission.VIEW_LTL_QUOTE_REQUEST,
          Permission.MANAGE_LTL_QUOTE_REQUEST,
        ],
        children: [
          {
            name: 'View LTL quotes',
            url: `/${AppRoutes.QUOTES}`,
            permission: Permission.VIEW_LTL_QUOTE_REQUEST,
          },
          {
            name: 'Create quote',
            url: `/${AppRoutes.QUOTES}/${QuotesRouting.QUOTE_MANAGER}/${QuoteManagerRouting.NEW_QUOTE}`,
            permission: Permission.MANAGE_LTL_QUOTE_REQUEST,
          },
          {
            name: 'Create quick quote',
            url: `/${AppRoutes.QUOTES}/${QuotesRouting.QUOTE_MANAGER}/${QuoteManagerRouting.QUICK_QUOTE}`,
            permission: Permission.MANAGE_LTL_QUOTE_REQUEST,
          },
          {
            name: 'Import quote',
            onClick: () => this.importQuote(),
            permission: Permission.MANAGE_LTL_QUOTE_REQUEST,
          },
        ],
      },
      {
        name: 'Shipments',
        url: `/${AppRoutes.SHIPMENTS}`,
        icon: 'twc:shipment',
        permission: [Permission.VIEW_SHIPMENT, Permission.IMPORT_SHIPMENT],
        children: [
          {
            name: 'View shipments',
            url: `/${AppRoutes.SHIPMENTS}`,
            permission: Permission.VIEW_SHIPMENT,
          },
          {
            name: 'Import shipment',
            onClick: () => this.importShipment(),
            permission: Permission.IMPORT_SHIPMENT,
          },
        ],
      },
    ];
  }
}
